import {organizationApi} from '@/api/organization.api.js'
import {useDataStore} from '@/store/data.store.js'
import {useTeams} from '@/composables/use-teams.js'
import {teammateDeliveryChannels} from '@/constants/teammate.js'
import {storeToRefs} from 'pinia'
import {ref, computed} from 'vue'

//Loading flag is global for all composable instances
const isLoadingOrganization = ref(false)

/**
 * Current organization composable
 */
export function useCurrentOrganization() {

  //Get store
  const store = useDataStore()
  const {organization} = storeToRefs(store)
  const {setOrganization} = store

  //Teams context
  const {isInTeams} = useTeams()

  //Load organization
  const loadOrganization = async (force = false) => {

    //Already loaded, we don't need to load again unless forcing to reload
    if (organization.value && !force) {
      return
    }

    //Already loading
    if (isLoadingOrganization.value) {
      return
    }

    //Load org
    try {
      isLoadingOrganization.value = true
      const loadedOrganization = await organizationApi.getCurrent()

      //NOTE: There seems to be a delay between setting via the store setter,
      //and manually setting the value of the ref, which is causing problems
      //for the global resolve hook. Setting the ref value manually for now
      //seems to work around this issue.
      organization.value = loadedOrganization
      setOrganization(loadedOrganization)
    }
    finally {
      isLoadingOrganization.value = false
    }
  }

  //Clear organization
  const clearOrganization = () => setOrganization(null)

  //Expose organization data as refs
  const organizationId = computed(() => organization.value?.id)
  const organizationName = computed(() => organization.value?.name)
  const organizationLogoUrl = computed(() => organization.value?.signedImageUrl)
  const allowUsersToManageEnrollments = computed(() => organization.value?.allowUsersToManageEnrollments)
  const showTeamsAppVersionWarning = computed(() => organization.value?.showTeamsAppVersionWarning)
  const teamsAppVersion = computed(() => organization.value?.teamsAppVersion)

  //Messenger-specific menu settings
  const showMessengerHelpMenuItem = computed(() => organization.value?.showMessengerHelpMenuItem)
  const showMessengerEnrollMenuItem = computed(() => organization.value?.showMessengerEnrollMenuItem)
  const showMessengerFeedbackMenuItem = computed(() => organization.value?.showMessengerFeedbackMenuItem)
  const messengerFeedbackUrl = computed(() => organization.value?.messengerFeedbackUrl)

  //Teams-specific copies of the messenger menu settings
  const showTeamsHelpMenuItem = computed(() => organization.value?.showTeamsHelpMenuItem)
  const showTeamsEnrollMenuItem = computed(() => organization.value?.showTeamsEnrollMenuItem)
  const showTeamsFeedbackMenuItem = computed(() => organization.value?.showTeamsFeedbackMenuItem)
  const teamsFeedbackUrl = computed(() => organization.value?.teamsFeedbackUrl)

  //Derived menu settings: use the Teams-specific setting when running in Teams,
  //otherwise use the messenger setting. The messenger setting is also used as a
  //fallback when the Teams-specific value is missing (e.g. not yet backfilled).
  const showHelpMenuItem = computed(() =>
    isInTeams.value ? showTeamsHelpMenuItem.value ?? showMessengerHelpMenuItem.value : showMessengerHelpMenuItem.value
  )
  const showEnrollMenuItem = computed(() =>
    isInTeams.value ? showTeamsEnrollMenuItem.value ?? showMessengerEnrollMenuItem.value : showMessengerEnrollMenuItem.value
  )
  const showFeedbackMenuItem = computed(() =>
    isInTeams.value ? showTeamsFeedbackMenuItem.value ?? showMessengerFeedbackMenuItem.value : showMessengerFeedbackMenuItem.value
  )
  const feedbackUrl = computed(() =>
    isInTeams.value ? teamsFeedbackUrl.value ?? messengerFeedbackUrl.value : messengerFeedbackUrl.value
  )

  //Agent (teammate) delivery channel. The agent surface in this app — the
  //agent view, its home card and deep links — only exists when the org routes
  //agent delivery to the tab. Any other value (or none) means the agent talks
  //to employees elsewhere (e.g. Slack), and a second surface would split the
  //same conversation across two places.
  const isAgentSurfaceEnabled = computed(() =>
    organization.value?.teammateDeliveryChannel === teammateDeliveryChannels.TEAMS
  )

  //Expose interface
  return {

    //Refs
    organization,
    organizationId,
    organizationName,
    organizationLogoUrl,
    isLoadingOrganization,
    allowUsersToManageEnrollments,
    showTeamsAppVersionWarning,
    teamsAppVersion,
    showHelpMenuItem,
    showEnrollMenuItem,
    showFeedbackMenuItem,
    feedbackUrl,
    isAgentSurfaceEnabled,

    //Methods
    loadOrganization,
    clearOrganization,
  }
}
